/**
 * DailyRitualsSection Component
 * Horizontal carousel of large featured ritual cards showing active practice rituals.
 * Includes left/right navigation and keyboard accessibility.
 */

import { ChevronRight } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { RitualCard } from "@/components/cards/ritualCard"
import { Carousel } from "@/components/carousel/Carousel"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useToggleRitualSave } from "@/features/profile/hooks/useSavedRituals"
import { formatRitualType } from "@/features/rituals/types"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"
import { useDailyRituals } from "../hooks/useDailyRituals"

export function DailyRitualsSection() {
  const { t } = useTranslation("myRituality")
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const toggleSave = useToggleRitualSave()

  const { data, isLoading } = useDailyRituals(user?.id || "")
  const rituals = data?.rituals || []

  if (isLoading) {
    return (
      <section>
        <div className="h-6 w-48 bg-white/5 rounded animate-pulse mb-8" />
        <div className="h-[310px] md:h-[620px] bg-white/5 rounded-tl-[40px] rounded-br-[40px] animate-pulse" />
      </section>
    )
  }

  if (rituals.length === 0) {
    return (
      <section>
        <h2 className="text-lg md:text-2xl font-semibold leading-[30px] uppercase text-[#F2F0EB]">
          {t("dailyRituals.title")}
        </h2>
        <div className="relative h-[310px] md:h-[620px] mt-4 rounded-tl-[40px] rounded-br-[40px] overflow-hidden">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url("${getAssetUrl("ritual_image.png")}")`,
            }}
          />

          {/* Overlay Content */}
          <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
            <h3 className="text-lg md:text-3xl font-semibold leading-5 md:leading-10 text-[#F2F0EB] mb-3.75 md:mb-5 md:uppercase">
              {t("dailyRituals.emptyTitle")}
            </h3>
            <p className="text-sm leading-5.5 text-[#F2F0EB] max-w-md mb-5 md:mb-7.5">
              {t("dailyRituals.emptyDescDetail")}
            </p>

            {/* Buttons */}
            <div className="flex flex-col-reverse md:flex-col items-center justify-center sm:flex-row gap-2.5 md:gap-3.75 w-full">
              <Button
                variant="outline"
                onClick={() => navigate("/rituals/create")}
                className="bg-transparent max-w-[280px] h-[50px] w-full py-3.75 rounded-full uppercase"
              >
                {t("dailyRituals.addYourOwn")}
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate("/learn-and-practice")}
                className="max-w-[280px] h-[50px] w-full py-3.75 rounded-full uppercase"
              >
                {t("dailyRituals.exploreRituals")}
              </Button>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section>
      {/* Header */}
      <div className="flex items-center justify-between mb-3.75 md:mb-8">
        <h2 className="text-lg md:text-2xl font-semibold leading-7.5 uppercase opacity-90 text-primary">
          {t("dailyRituals.title")}
        </h2>
        <button
          type="button"
          onClick={() => navigate("/learn-and-practice")}
          className="hidden md:flex items-center gap-1 text-sm font-semibold text-primary hover:opacity-80 transition-opacity cursor-pointer"
        >
          <span>{t("dailyRituals.showAll")}</span>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Carousel */}
      <Carousel
        data={rituals}
        renderItem={ritual => {
          const coverImage = ritual.uploads?.find(u => u.isCover)?.filePath
          return (
            <div className="h-[260px] md:h-[620px]">
              <RitualCard
                id={ritual.id}
                image={coverImage}
                userAvatar={ritual.createdBy?.coverImage || undefined}
                username={ritual.createdBy?.name || "Unknown"}
                userHandle={ritual.createdBy?.username ? `@${ritual.createdBy.username}` : ""}
                title={ritual.title}
                tags={[formatRitualType(ritual.type)]}
                duration={formatDuration(ritual.duration)}
                isBookmarked={ritual.isSaved}
                onBookmarkClick={() => toggleSave.mutate(ritual.id)}
                userId={ritual.createdBy?.id}
                price={ritual.price ?? undefined}
                participationFee={ritual.participationFee}
              />
            </div>
          )
        }}
        itemsPerScreen={{ sm: 1, md: 1, lg: 1, xl: 1 }}
        opts={{ loop: false, dragFree: false, align: "start" }}
      />
      <button
        type="button"
        onClick={() => navigate("/learn-and-practice")}
        className="flex md:hidden mt-7.5 items-center gap-1.25 text-sm font-semibold text-primary hover:opacity-80 transition-opacity cursor-pointer"
      >
        <span>{t("dailyRituals.showAll")}</span>
        <ChevronRight className="w-5 h-5" />
      </button>
    </section>
  )
}
